import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";

/** Resolve a stored image reference (legacy URL or storage_path) to a full URL. */
function resolveImage(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${API}/files/${p}`;
}

/** Gallery editor for the current Créateur (logged in).
 * Backed by:
 *   POST   /api/pros/me/gallery        (multipart file)
 *   DELETE /api/pros/me/gallery?storage_path=...
 *   PUT    /api/pros/me/gallery/reorder  { gallery_images: [...] }
 */
export default function CreatorGalleryEditor({ initialGallery = [], onChange }) {
  const [gallery, setGallery] = useState(initialGallery);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const update = (next) => {
    setGallery(next);
    onChange?.(next);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let latest = gallery;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/pros/me/gallery", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latest = data.gallery_images || latest;
      }
      update(latest);
      toast.success(files.length === 1 ? "Photo ajoutée" : `${files.length} photos ajoutées`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.userMessage || "Échec de l'upload";
      toast.error(typeof msg === "string" ? msg : "Échec de l'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (path) => {
    const prev = gallery;
    update(gallery.filter((p) => p !== path));
    try {
      const { data } = await api.delete("/pros/me/gallery", { params: { storage_path: path } });
      update(data.gallery_images || []);
      toast.success("Photo supprimée");
    } catch (err) {
      update(prev);
      toast.error(err?.userMessage || "Suppression impossible");
    }
  };

  return (
    <div className="space-y-4" data-testid="creator-gallery-editor">
      <div className="flex items-center justify-between">
        <div>
          <p className="caption-label">Galerie publique</p>
          <p className="text-xs text-[#666] mt-1">Vos meilleures photos s'affichent sur votre profil public ({gallery.length}/12).</p>
        </div>
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || gallery.length >= 12}
          className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white"
          data-testid="creator-gallery-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {uploading ? "Envoi..." : "Ajouter"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={handleFileChange}
          data-testid="creator-gallery-input"
        />
      </div>

      {gallery.length === 0 ? (
        <div className="bg-[#FAF3EA] border border-dashed border-[#EADDC9] rounded-2xl p-10 text-center" data-testid="creator-gallery-empty">
          <ImageIcon className="w-8 h-8 text-[#C68E59] mx-auto" />
          <p className="font-serif text-lg text-[#1A1A1A] mt-3">Aucune photo pour l'instant</p>
          <p className="text-xs text-[#666] mt-1">Ajoutez 4 à 12 photos pour donner vie à votre univers.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {gallery.map((p) => (
            <div key={p} className="relative aspect-square rounded-2xl overflow-hidden border border-[#EADDC9] group" data-testid={`creator-gallery-item-${p}`}>
              <img
                src={resolveImage(p)}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <button
                type="button"
                onClick={() => handleDelete(p)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                aria-label="Supprimer"
                data-testid={`creator-gallery-delete-${p}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
